import { purchaseOrderDetail } from '../../model/purchase-order';

/**
 * Lines the auto-fill could not put a quantity on.
 *
 * "ดึงสินค้าใกล้หมด" used to guess when "จำนวนสั่งซื้อเริ่มต้น" was not set:
 * twice the order point minus stock, or ten. With a high order point and no
 * cap that guess ran away (order point 120, 100 in stock -> 140 ordered).
 * The shop's decision, 2026-09-09: do not guess. Pull the product in, leave
 * the quantity blank, put it at the TOP of the table so it cannot be missed,
 * and refuse to save or send until someone has typed a number.
 *
 * Blank lines sit above everything else in the order they arrived -- the
 * first one pulled or keyed is the first one down. A line keeps its place
 * once a quantity is typed; rows do not jump under the cursor.
 */

/**
 * `needs_quantity` marks a line the auto-fill pulled in without a default;
 * it stays on the line for good, so the cell may go back to blank when the
 * number is cleared instead of silently becoming 1 the way an ordinary
 * line does. A line is UNFINISHED while it carries that mark and has no
 * usable quantity.
 */
export const needsQuantity = (line: purchaseOrderDetail): boolean =>
    !!line.needs_quantity && !(Number(line.quantity) >= 1);

export const linesNeedingQuantity = (detail: purchaseOrderDetail[] | undefined): purchaseOrderDetail[] =>
    (detail || []).filter(needsQuantity);

/**
 * Inserts a blank line after the last blank line already at the top, so
 * blanks read top-down in arrival order and everything else stays below.
 */
export const insertBlankLine = (detail: purchaseOrderDetail[], line: purchaseOrderDetail): purchaseOrderDetail[] => {
    const next = [...detail];
    let at = 0;
    while (at < next.length && needsQuantity(next[at])) at++;
    next.splice(at, 0, { ...line, quantity: 0, total_cost: 0, needs_quantity: true });
    return next.map((d, i) => ({ ...d, key: i + 1 }));
};

export const describeBlankLines = (lines: purchaseOrderDetail[]): string => {
    const names = lines.map((l) => l.product_name);
    return names.slice(0, 3).join(', ') + (names.length > 3 ? ` และอีก ${names.length - 3} รายการ` : '');
};


/**
 * What the shop can physically sell of a product -- the number every
 * purchase-order decision compares against.
 *
 * The shop holds the same product as its own stock AND as consigned goods.
 * `quantity` counts only the first, so a product with none of the shop's
 * own but twenty consigned on the shelf looked out of stock and was pulled
 * into a purchase order. Backend now sends `total_sellable_quantity`: the
 * shop's own stock (never below zero) plus consigned goods sellable now.
 *
 * Falls back to `quantity` when that field is absent -- a Backend that
 * predates it, or a product read from a list that does not carry it (the
 * product picker's LIST) -- so this screen keeps working against either
 * and a mismatched deploy degrades to the old behaviour, not to a crash.
 */
export const poSellableQuantity = (product: any): number => {
    const raw = product?.total_sellable_quantity;
    if (raw !== null && raw !== undefined && raw !== '') {
        const unified = Number(raw);
        if (Number.isFinite(unified)) return unified;
    }
    const legacy = Number(product?.quantity ?? 0);
    return Number.isFinite(legacy) ? legacy : 0;
};

/**
 * Should "ดึงสินค้าใกล้หมดของ Vendor นี้" pull this product in?
 *
 * With a reorder point set (0 counts): when what the shop can sell is at or
 * below it -- whatever the own-stock state says, because consigned goods on
 * the shelf are just as sellable.
 *
 * Without one: only a product that has run out before (OUT) AND has nothing
 * consigned either. A never-stocked catalogue import with no reorder point
 * stays out, so thousands of unfinished products do not flood the order.
 *
 * `stock_state` itself is not reinterpreted: the rest of the app keeps
 * reading it as it always has.
 */
export const needsPoReorder = (product: any): boolean => {
    const point = product?.point_purchase;
    const hasPoint = point !== null && point !== undefined && point !== '' && Number.isFinite(Number(point));
    const sellable = poSellableQuantity(product);
    if (hasPoint) return sellable <= Number(point);
    return product?.stock_state === 'OUT' && sellable <= 0;
};
